import logoPrimary from '@assets/images/common/brand/logo-primary.svg';
import { handleApiResponse } from '@src/api/apiClient/axiosClient';
import { companyLoginApiClient } from '@src/api/company/auth/companyLoginApiClient';
import { companySiteApiClient } from '@src/api/company/site/companySiteApiClient';
import { fetchCompanyUserProfile } from '@src/context/auth/ProfileFetcher';
import { useCompanyAuth } from '@src/context/auth/useCompanyAuth';
import { useToast } from '@src/features/toast/context/toastContext';
import TOAST_MESSAGE from '@src/modules/constants/toastMessage';
import { SESSION_STORAGE_KEYS } from '@src/constants/sessionStorage';
import Button from '@src/ui/elements/button/Button';
import { ErrorMessage } from '@src/ui/elements/form';
import { isValidDxstarUrl } from '@src/utils/redirectAfterLogin';
import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

interface LocationState {
  email: string;
  password: string;
  is_first_login: boolean;
  session: string;
  redirect?: string;
}

type TwoFactorErrors = {
  messages?: string[];
};

const RESUBMIT_WAIT_SECONDS = 60;

export default function TwoFactorForm() {
  const navigate = useNavigate();
  const [session, setSession] = useState<string | null>(null);
  const { state } = useLocation() as { state: LocationState };
  const toast = useToast();

  // フォーム state
  const [code, setCode] = useState('');
  const [errors, setErrors] = useState<TwoFactorErrors>({});
  const [isResendPressed, setIsResendPressed] = useState(false);
  const [isSubmitButtonPressed, setIsSubmitButtonPressed] = useState(false);
  const companyAuth = useCompanyAuth();

  useEffect(() => {
    if (isResendPressed) {
      setTimeout(() => {
        setIsResendPressed(false);
      }, RESUBMIT_WAIT_SECONDS * 1000);
    }
  }, [isResendPressed]);

  const onSubmit = async () => {
    setIsSubmitButtonPressed(true);
    // OTP 検証
    const res = await companyLoginApiClient.confirmTwoFactorCode({
      email: state.email,
      session: session ?? state.session,
      code,
    });

    handleApiResponse(
      res,
      async () => {
        fetchCompanyUserProfile(companyAuth.setProfile);

        if (state.redirect && isValidDxstarUrl(state.redirect)) {
          sessionStorage.removeItem(SESSION_STORAGE_KEYS.REDIRECT_AFTER_LOGIN);
          navigate(state.redirect);
          return;
        }

        const topRes = await companySiteApiClient.topPageUrl();
        handleApiResponse(
          topRes,
          (topData) => {
            navigate(topData.url);
          },
          () => {
            setIsSubmitButtonPressed(false);
          },
        );
      },
      (errs) => {
        setIsSubmitButtonPressed(false);
        if (errs?.code) {
          setErrors({ messages: Array.isArray(errs.code) ? errs.code : [errs.code] });
          return;
        }
        if (errs?.messages) {
          setErrors({ messages: Array.isArray(errs.messages) ? errs.messages : [errs.messages] });
          return;
        }
        setErrors({});
      },
    );
  };

  return (
    <div className='flex flex-col gap-4'>
      <div className='grid place-items-center'>
        <img
          alt={import.meta.env.VITE_APP_NAME}
          className='h-auto max-h-[72px] w-[400px] object-contain'
          src={logoPrimary}
        />
      </div>
      <div className='flex flex-col gap-1'>
        <span className='text-center font-medium'>
          メールに記載されている
          <br />
          認証コードを入力してください。
        </span>
      </div>
      <span className='font-medium'>認証コード</span>
      <input
        type='text'
        value={code}
        onChange={(e) => setCode(e.target.value)}
        placeholder='認証コード'
        className={`input-login ${errors.messages ? 'error-input' : ''}`}
      />
      {errors.messages?.map((m, i) => <ErrorMessage key={i}>{m}</ErrorMessage>)}
      <p className='mt-4 text-center text-sm'>
        {isResendPressed ? (
          <span className='text-black'>{RESUBMIT_WAIT_SECONDS}秒後に再送信できます</span>
        ) : (
          <button
            className='text-primary-500 underline'
            onClick={() => {
              setIsResendPressed(true);

              // コード再送信
              companyLoginApiClient
                .requestTwoFactorCode({
                  email: state.email,
                  password: state.password,
                })
                .then((r) =>
                  handleApiResponse(r, (data) => {
                    setSession(data.session);
                    toast(TOAST_MESSAGE.SEND);
                  }),
                );
            }}
          >
            認証コードの再送信
          </button>
        )}
      </p>
      <Button
        fullWidth
        fullRounded
        className='mt-4'
        disabled={isSubmitButtonPressed}
        onClick={onSubmit}
      >
        認証
      </Button>
    </div>
  );
}
